import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { collection, query, where, onSnapshot } from "firebase/firestore";
import { signOut } from "firebase/auth";
import { auth, db } from "../firebase";
import styles from "../styles/AdminConsole.module.css";
import { useToast } from "./admin/shared/hooks/useToast";

// Tab components — each owns its own Firebase subscriptions and local state
import SuppliersTab from "./admin/tabs/SuppliersTab";
import TeamTab from "./admin/tabs/TeamTab";
import OrdersTab from "./admin/tabs/OrdersTab";
import LeadsTab from "./admin/tabs/LeadsTab";
import DatabaseTab from "./admin/tabs/DatabaseTab";
import QcFlagsTab from "./admin/tabs/QcFlagsTab";
const LOGOURL =
  "https://cdn.synaptexglobal.com/Home%20(3240%20x%201080%20px).png";

const TABS = [
  { key: "suppliers", label: "Suppliers" },
  { key: "team", label: "Team" },
  { key: "orders", label: "Order Profile" },
  { key: "leads", label: "Leads" },
  { key: "database", label: "Database" },
  { key: "qcflags", label: "QC Flags" },
];

export default function AdminConsole() {
  const navigate = useNavigate();
  const { toast, showToast } = useToast();
  const [activeTab, setActiveTab] = useState("suppliers");

  // ── Shared subscription: users list (needed by Suppliers + Team + Orders) ─
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);

  useEffect(() => {
    const q = query(collection(db, "users"), where("isAdmin", "==", false));
    const unsub = onSnapshot(
      q,
      (snap) => {
        setUsers(snap.docs.map((d) => ({ uid: d.id, ...d.data() })));
        setLoadingUsers(false);
      },
      (err) => {
        console.error("Failed to fetch users:", err);
        setLoadingUsers(false);
      },
    );
    return () => unsub();
  }, []);

  const handleLogout = useCallback(async () => {
    await signOut(auth);
    navigate("/");
  }, [navigate]);

  return (
    <div className={styles.page}>
      {/* ── Top Bar ── */}
      <div className={styles.topBar}>
        <div className={styles.topLogo}>
          <img src={LOGOURL} className={styles.logoImg} alt="Synaptex" />
        </div>
        <nav className={styles.navRight}>
          <button
            className={`${styles.navBtn} ${styles.navBtnActive}`}
            onClick={handleLogout}
          >
            Sign Out
          </button>
        </nav>
      </div>

      {/* ── Welcome Bar ── */}
      <div className={styles.welcomeBar}>
        <span className={styles.welcomeText}>
          WELCOME,{" "}
          <span
            className={styles.welcomeName}
            style={{ textTransform: "uppercase" }}
          >
            {auth.currentUser?.displayName ||
              auth.currentUser?.email?.split("@")[0]}
            !
          </span>
        </span>
        <h1 className={styles.pageTitle}>Admin Console</h1>
      </div>

      <div className={styles.rule} />

      {/* ── Tab Bar ── */}
      <div className={styles.tabBar}>
        {TABS.map(({ key, label }) => (
          <button
            key={key}
            className={`${styles.tab} ${activeTab === key ? styles.tabActive : ""}`}
            onClick={() => setActiveTab(key)}
          >
            {label}
          </button>
        ))}
      </div>

      {/* ── Tab Content ─────────────────────────────────────────────────────
          Each tab component is only mounted when active (conditional render),
          so its Firebase subscriptions only open when the user visits that tab.
      ── */}
      {!loadingUsers && (
        <>
          {activeTab === "suppliers" && (
            <SuppliersTab
              users={users}
              setUsers={setUsers}
              showToast={showToast}
            />
          )}
          {activeTab === "team" && (
            <TeamTab users={users} setUsers={setUsers} showToast={showToast} />
          )}
          {activeTab === "orders" && (
            <OrdersTab users={users} showToast={showToast} />
          )}
          {activeTab === "leads" && <LeadsTab showToast={showToast} />}
          {activeTab === "database" && <DatabaseTab showToast={showToast} />}
          {activeTab === "qcflags" && <QcFlagsTab showToast={showToast} />}
        </>
      )}

      {/* ── Toast ── */}
      {toast && (
        <div className={styles.toast}>
          <div
            className={
              toast.type === "error" ? styles.toastError : styles.toastSuccess
            }
          >
            {toast.text}
          </div>
        </div>
      )}
    </div>
  );
}
